import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, List, Tag, Spin, Empty, Typography, Divider, message } from 'antd';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { 
  SearchOutlined, 
  ClockCircleOutlined, 
  LikeOutlined,
  DislikeOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { getAllFaqData, getSolutionCategories } from '../../services/freshdeskService';
import { CACHE_KEYS } from '../../store/queryClient';
import styles from './Search.module.css';

// HTML tag'lerini temizleyen utility fonksiyon
const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// Tarih formatını düzenleyen utility fonksiyon
const formatDate = (dateString) => {
  if (!dateString) return 'Unknown date';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return dateString;
  }
};

const { Title, Text } = Typography;

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = useState([]);
  const [allSearchResults, setAllSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('relevance');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // Tüm FAQ verisini çek
  const { data: allFaqData, isLoading: isFaqLoading } = useQuery({
    queryKey: CACHE_KEYS.ALL_FAQ_DATA('en'),
    queryFn: () => getAllFaqData('en'),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // Kategorileri yükle
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const categoriesData = await getSolutionCategories();
      
      // API'den gelen veri formatına göre işle
      const categories = categoriesData.data || categoriesData;
      
      // Her kategori objesinin doğru formatda olduğundan emin ol
      const validCategories = categories.filter(cat => 
        cat && typeof cat === 'object' && cat.id && cat.name
      );
      
      // "All Categories" seçeneğini ekle
      const allCategories = [
        { id: 'all', name: 'All Categories', count: 0 },
        ...validCategories.map(cat => ({ ...cat, count: 0 }))
      ];
      
      setCategories(allCategories);
    } catch (error) {
      console.error('Error loading categories:', error);
      // Fallback categories
      setCategories([
        { id: 'all', name: 'All Categories', count: 0 },
        { id: 'suppliers', name: 'Suppliers\' FAQs', count: 0 },
        { id: 'registration', name: 'Registration and Verification', count: 0 },
        { id: 'login', name: 'Login Issues', count: 0 },
        { id: 'account', name: 'Managing your account', count: 0 },
        { id: 'products', name: 'Creating and managing products', count: 0 },
        { id: 'performance', name: 'Product Performance', count: 0 }
      ]);
    }
  };

  // Arama sonuçlarına göre kategori sayılarını hesapla
  const calculateCategoryCounts = useCallback((searchResults) => {
    const categoryCounts = {};
    
    // Tüm sonuçları say
    searchResults.forEach(result => {
      const categoryId = result.category_id;
      if (categoryId) {
        categoryCounts[categoryId] = (categoryCounts[categoryId] || 0) + 1;
      }
    });
    
    // Kategorileri güncelle
    setCategories(prevCategories => 
      prevCategories.map(category => ({
        ...category,
        count: category.id === 'all' 
          ? searchResults.length 
          : categoryCounts[category.id] || 0
      }))
    );
  }, []);

  // Arama fonksiyonu - mevcut veriyi kullanarak client-side search
  const performSearch = useCallback((query) => {
    if (!query.trim()) return;
    
    setIsLoading(true);
    
    try {
      // Aktif arama terimini güncelle
      setActiveSearchQuery(query.trim());
      
      // URL'yi güncelle
      navigate(`/search?query=${encodeURIComponent(query.trim())}`);
      
      // Client-side search - mevcut FAQ verisini kullan
      const allArticles = [];
      const categories = allFaqData?.data?.categories || [];
      
      categories.forEach(category => {
        if (category.folders && Array.isArray(category.folders)) {
          category.folders.forEach(folder => {
            if (folder.articles && Array.isArray(folder.articles)) {
              folder.articles.forEach(article => {
                allArticles.push({
                  ...article,
                  category_name: category.name,
                  folder_name: folder.name,
                  category_id: category.id,
                  folder_id: folder.id
                });
              });
            }
          });
        }
      });

      // Query'ye göre filtreleme
      const searchTerm = query.toLowerCase().trim();
      const filteredResults = allArticles.filter(article => {
        const title = (article.title || '').toLowerCase();
        const description = (article.description || article.body || '').toLowerCase();
        const category = (article.category_name || '').toLowerCase();
        const folder = (article.folder_name || '').toLowerCase();
        
        return title.includes(searchTerm) || 
               description.includes(searchTerm) || 
               category.includes(searchTerm) || 
               folder.includes(searchTerm);
      });

      // Sıralama
      let sortedResults = [...filteredResults];
      if (sortBy === 'date') {
        sortedResults.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
      } else if (sortBy === 'views') {
        sortedResults.sort((a, b) => (b.hits || b.view_count || 0) - (a.hits || a.view_count || 0));
      } else {
        // Relevance - title'da geçenler önce
        sortedResults.sort((a, b) => {
          const aTitle = (a.title || '').toLowerCase();
          const bTitle = (b.title || '').toLowerCase();
          const aInTitle = aTitle.includes(searchTerm);
          const bInTitle = bTitle.includes(searchTerm);
          
          if (aInTitle && !bInTitle) return -1;
          if (!aInTitle && bInTitle) return 1;
          return 0;
        });
      }

      // Kategori sayılarını hesapla (filtreleme öncesi)
      calculateCategoryCounts(filteredResults);

      // Kategori filtresi
      if (selectedCategory && selectedCategory !== 'all') {
        sortedResults = sortedResults.filter(article => 
          article.category_id === parseInt(selectedCategory)
        );
      }
      
      setAllSearchResults(sortedResults);
      setSearchResults(sortedResults);
      setTotalResults(sortedResults.length);
      setCurrentPage(1); // Yeni arama yapıldığında ilk sayfaya dön
      
    } catch (error) {
      console.error('Search error:', error);
      message.error('Search failed. Please try again.');
      setSearchResults([]);
      setTotalResults(0);
    } finally {
      setIsLoading(false);
    }
  }, [allFaqData, sortBy, selectedCategory, navigate, calculateCategoryCounts]);

  // URL'den query parametresini al
  useEffect(() => {
    const query = searchParams.get('query');
    if (query && allFaqData) {
      setSearchQuery(query);
      setActiveSearchQuery(query);
      performSearch(query);
    }
  }, [searchParams, allFaqData, performSearch]);

  const handleCategoryFilter = useCallback((categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1); // Kategori değiştiğinde ilk sayfaya dön
    
    if (activeSearchQuery) {
      // Mevcut sonuçları yeniden filtrele
      performSearch(activeSearchQuery);
    }
  }, [activeSearchQuery, performSearch]);

  const handleSortChange = useCallback((newSortBy) => {
    setSortBy(newSortBy);
    setCurrentPage(1); // Sayfa değişikliğinde ilk sayfaya dön
    
    if (activeSearchQuery) {
      // Mevcut sonuçları yeniden sırala
      performSearch(activeSearchQuery);
    }
  }, [activeSearchQuery, performSearch]);

  // Pagination fonksiyonları
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);

  const handleNextPage = useCallback(() => {
    const totalPages = Math.ceil(totalResults / pageSize);
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, totalResults, pageSize]);

  const handleLastPage = useCallback(() => {
    const totalPages = Math.ceil(totalResults / pageSize);
    setCurrentPage(totalPages);
  }, [totalResults, pageSize]);

  // Mevcut sayfa için sonuçları hesapla
  const currentPageResults = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return allSearchResults.slice(startIndex, endIndex);
  }, [allSearchResults, currentPage, pageSize]);

  // Toplam sayfa sayısını hesapla
  const totalPages = useMemo(() => {
    return Math.ceil(totalResults / pageSize);
  }, [totalResults, pageSize]);

  const renderSearchResult = (result) => (
    <div className={styles.searchResult}>
      <div className={styles.resultHeader}>
        <Link to={`/faq/article/${result.id}`} className={styles.resultTitle}>
          {result.title}
        </Link>
        <div className={styles.resultMeta}>
          <Text type="secondary" className={styles.category}>
            {result.category_name || 
             (result.category && typeof result.category === 'object' ? result.category.name : result.category) ||
             (result.category_id && categories.find(c => c.id === result.category_id)?.name) || 
             (result.folder && typeof result.folder === 'object' ? result.folder.name : result.folder_name) || 
             'Uncategorized'}
          </Text>
          <div className={styles.stats}>
            <span className={styles.date}>
              <ClockCircleOutlined /> {formatDate(result.updated_at || result.date)}
            </span>
            {result.view_count !== undefined && (
              <span className={styles.views}>
                <EyeOutlined /> {result.view_count}
              </span>
            )}
            {result.votes !== undefined && result.votes !== 0 && (
              <span className={`${styles.votes} ${result.votes > 0 ? styles.positive : styles.negative}`}>
                {result.votes > 0 ? <LikeOutlined /> : <DislikeOutlined />} {Math.abs(result.votes)}
              </span>
            )}
          </div>
        </div>
      </div>
      
      <div className={styles.resultExcerpt}>
        {(() => {
          const description = stripHtml(result.excerpt || result.description || result.body);
          if (!description) return 'No description available';
          
          // İlk 150 karakteri al ve eğer daha uzunsa "..." ekle
          const truncated = description.length > 150 
            ? description.substring(0, 150) + '...' 
            : description;
          
          return truncated;
        })()}
      </div>
    </div>
  );

  return (
    <div className={styles.searchPage}>
      {/* Header Section */}
      <div className={styles.header}>
        <div className={styles.container}>
          <Title level={1} className={styles.pageTitle}>
            Need help? We're here for you.
          </Title>
          <p className={styles.subtitle}>
            Search our knowledge base for answers to common questions
          </p>
          
          <div className={styles.searchBar}>
            <div className={styles.searchInput}>
              <SearchOutlined className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Enter a search term and press Enter"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    performSearch(searchQuery);
                  }
                }}
              />
            </div>
          </div>

          <div className={styles.commonTopics}>
            <span>Common topics: </span>
            {['account management', 'product', 'booking support', 'payment', 'sales', 'reports'].map((topic, index) => (
              <span 
                key={index}
                className={styles.topic}
                onClick={() => {
                  setSearchQuery(topic);
                  performSearch(topic);
                }}
              >
                {topic}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <div className={styles.container}>
          <div className={styles.contentGrid}>
            {/* Left Sidebar */}
            <div className={styles.sidebar}>
              <Card 
                title="By Category" 
                className={styles.categoryCard}
                headStyle={{ 
                  fontSize: '16px', 
                  fontWeight: 600, 
                  borderBottom: '1px solid #f0f0f0',
                  padding: '16px 20px'
                }}
                bodyStyle={{ padding: '0' }}
              >
                <div className={styles.categoryList}>
                  {categories.map((category) => {
                    // Kategori objesinin geçerli olduğundan emin ol
                    if (!category || typeof category !== 'object' || !category.id || !category.name) {
                      return null;
                    }
                    
                    return (
                      <div 
                        key={category.id}
                        className={`${styles.categoryItem} ${selectedCategory === category.id ? styles.active : ''}`}
                        onClick={() => handleCategoryFilter(category.id)}
                      >
                        <span>{category.name}</span>
                        <Tag color="blue">{category.count || 0}</Tag>
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Sort Options */}
              <Card 
                title="Sort By" 
                className={styles.sortCard}
                headStyle={{ 
                  fontSize: '16px', 
                  fontWeight: 600, 
                  borderBottom: '1px solid #f0f0f0',
                  padding: '16px 20px'
                }}
                bodyStyle={{ padding: '0' }}
              >
                <div className={styles.sortList}>
                  <div 
                    className={`${styles.sortItem} ${sortBy === 'relevance' ? styles.active : ''}`}
                    onClick={() => handleSortChange('relevance')}
                  >
                    Relevance
                  </div>
                  <div 
                    className={`${styles.sortItem} ${sortBy === 'date' ? styles.active : ''}`}
                    onClick={() => handleSortChange('date')}
                  >
                    Date
                  </div>
                  <div 
                    className={`${styles.sortItem} ${sortBy === 'views' ? styles.active : ''}`}
                    onClick={() => handleSortChange('views')}
                  >
                    Views
                  </div>
                </div>
              </Card>
            </div>

            {/* Right Section - Search Results */}
            <div className={styles.resultsSection}>
              {isFaqLoading || isLoading ? (
                <div className={styles.loadingContainer}>
                  <Spin size="large" />
                  <p>Searching...</p>
                </div>
              ) : currentPageResults.length > 0 ? (
                <div>
                  <Title level={2} className={styles.resultsTitle}>
                    {totalResults} results for "{activeSearchQuery}" in {selectedCategory === 'all' ? 'All Categories' : categories.find(c => c.id === selectedCategory)?.name || 'All Categories'}
                  </Title>
                  
                  <div className={styles.resultsList}>
                    {currentPageResults.map((result) => (
                      <div key={result.id} className={styles.resultItem}>
                        {renderSearchResult(result)}
                        <Divider style={{ margin: '16px 0' }} />
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className={styles.pagination}>
                      <span className={styles.paginationInfo}>
                        Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalResults)} of {totalResults} results
                      </span>
                      <div className={styles.paginationButtons}>
                        <button 
                          className={`${styles.paginationButton} ${currentPage === 1 ? styles.disabled : ''}`}
                          onClick={handlePreviousPage}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                        <span className={styles.pageInfo}>
                          Page {currentPage} of {totalPages}
                        </span>
                        <button 
                          className={`${styles.paginationButton} ${currentPage === totalPages ? styles.disabled : ''}`}
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                        >
                          Next ›
                        </button>
                        <button 
                          className={`${styles.paginationButton} ${currentPage === totalPages ? styles.disabled : ''}`}
                          onClick={handleLastPage}
                          disabled={currentPage === totalPages}
                        >
                          Last »
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : searchQuery ? (
                <div className={styles.noResults}>
                  <Empty
                    description={
                      <div>
                        <Title level={3}>No results found for "{searchQuery}"</Title>
                        <p>Try different keywords or check your spelling</p>
                      </div>
                    }
                  />
                </div>
              ) : (
                <div className={styles.welcomeState}>
                  <Title level={2}>Search Our Knowledge Base</Title>
                  <p>Enter your search terms above to find articles, guides, and solutions.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage; 